import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { retainBed } from "../../api/bedRetentionApi";

const DURATION_OPTIONS = [
  { label: "2 hours", hours: 2 },
  { label: "6 hours", hours: 6 },
  { label: "12 hours", hours: 12 },
  { label: "24 hours", hours: 24 },
  { label: "48 hours", hours: 48 },
];

// Retain a freed (available) bed for a fixed duration — e.g. the patient is
// expected back for follow-up treatment. Sets the bed to "reserved".
export default function RetainBedModal({ open, onClose, bedCode, admissionId, onRetained }) {
  const [durationHours, setDurationHours] = useState(24);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const expiresAt = new Date(Date.now() + durationHours * 60 * 60 * 1000).toISOString();
      const data = await retainBed({
        bedCode,
        admissionId,
        expiresAt,
        reason: reason.trim() || undefined,
      });
      if (data?.status === "Success") {
        toast.success(data.message || `Bed ${bedCode} retained.`);
        onRetained?.(data);
        onClose?.();
      } else {
        toast.error(data?.message || "Could not retain the bed.");
      }
    } catch (err) {
      console.error("Retain bed failed:", err);
      toast.error(err?.response?.data?.message || "Could not retain the bed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Retain Bed</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "14px", mb: 1.5 }}>
          Reserve bed <strong>{bedCode}</strong> for a fixed period. The bed
          will be marked "reserved" until released or the duration expires.
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel>Duration</InputLabel>
          <Select
            label="Duration"
            value={durationHours}
            onChange={(e) => setDurationHours(Number(e.target.value))}
          >
            {DURATION_OPTIONS.map((opt) => (
              <MenuItem key={opt.hours} value={opt.hours}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting}>
          {submitting ? "Retaining..." : "Retain Bed"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
